import { defineComponent, mergeDefaults, warn, useAttrs, getCurrentInstance, onBeforeMount, onMounted, onBeforeUnmount, onUnmounted, onBeforeUpdate, onUpdated, ref, openBlock, createBlock, unref, withCtx, renderSlot } from "vue";
import { injectMotion, injectLayoutGroup, provideMotion } from "../context.mjs";
import { convertSvgStyleToAttributes, createStyles } from "../../state/style.mjs";
import { Primitive } from "./Primitive.mjs";
import { MotionState } from "../../state/motion-state.mjs";
import { injectAnimatePresence } from "../presence.mjs";
import { checkMotionIsHidden } from "./utils.mjs";
import { useMotionConfig } from "../motion-config/context.mjs";
import { getMotionElement } from "../hooks/use-motion-elm.mjs";
import { isMotionValue } from "../../utils/motion-value.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  ...{
    name: "Motion",
    inheritAttrs: false
  },
  __name: "Motion",
  props: /* @__PURE__ */ mergeDefaults({
    as: {},
    asChild: { type: Boolean },
    hover: {},
    press: {},
    inView: {},
    focus: {},
    whileDrag: {},
    whileHover: {},
    whilePress: {},
    whileInView: {},
    whileFocus: {},
    forwardMotionProps: { type: Boolean },
    custom: {},
    initial: { type: [String, Array, Object, Boolean] },
    animate: {},
    exit: {},
    variants: {},
    inherit: { type: Boolean },
    style: {},
    transformTemplate: { type: Function },
    transition: {},
    layoutGroup: {},
    motionConfig: {},
    onAnimationComplete: { type: Function },
    onUpdate: { type: Function },
    layout: { type: [Boolean, String] },
    layoutId: {},
    layoutScroll: { type: Boolean },
    layoutRoot: { type: Boolean },
    "data-framer-portal-id": {},
    crossfade: { type: Boolean },
    layoutDependency: {},
    onBeforeLayoutMeasure: { type: Function },
    onLayoutMeasure: { type: Function },
    onLayoutAnimationStart: { type: Function },
    onLayoutAnimationComplete: { type: Function },
    globalPressTarget: { type: Boolean },
    onPressStart: { type: Function },
    onPress: { type: Function },
    onPressCancel: { type: Function },
    onHoverStart: { type: Function },
    onHoverEnd: { type: Function },
    inViewOptions: {},
    onViewportEnter: { type: Function },
    onViewportLeave: { type: Function },
    drag: { type: [Boolean, String] },
    dragSnapToOrigin: { type: Boolean },
    dragDirectionLock: { type: Boolean },
    dragPropagation: { type: Boolean },
    dragConstraints: { type: [Boolean, Object] },
    dragElastic: { type: [Boolean, Number, Object] },
    dragMomentum: { type: Boolean },
    dragTransition: {},
    dragListener: { type: Boolean },
    dragControls: {},
    onDragStart: { type: Function },
    onDragEnd: { type: Function },
    onDrag: { type: Function },
    onDirectionLock: { type: Function },
    onDragTransitionEnd: { type: Function },
    onMeasureDragConstraints: { type: Function },
    onPanSessionStart: { type: Function },
    onPanStart: { type: Function },
    onPan: { type: Function },
    onPanEnd: { type: Function },
    onFocus: { type: Function },
    onBlur: { type: Function }
  }, {
    as: "div",
    asChild: false,
    initial: void 0,
    animate: void 0,
    hover: void 0,
    inView: void 0,
    layout: false,
    layoutId: void 0,
    layoutScroll: false,
    layoutRoot: false,
    dragListener: true,
    dragElastic: 0.5,
    dragMomentum: true,
    whileDrag: void 0,
    crossfade: true,
    whileHover: ({ hover }) => {
      if (process.env.NODE_ENV === "development" && hover) {
        warn("hover is deprecated. Use whileHover instead.");
      }
      return hover;
    },
    whilePress: ({ press }) => {
      if (process.env.NODE_ENV === "development" && press) {
        warn("press is deprecated. Use whilePress instead.");
      }
      return press;
    },
    whileInView: ({ inView }) => {
      if (process.env.NODE_ENV === "development" && inView) {
        warn("inView is deprecated. Use whileInView instead.");
      }
      return inView;
    },
    whileFocus: ({ focus }) => {
      if (process.env.NODE_ENV === "development" && focus) {
        warn("focus is deprecated. Use whileFocus instead.");
      }
      return focus;
    }
  }),
  setup(__props) {
    const props = __props;
    const animatePresenceContext = injectAnimatePresence({});
    const parentState = injectMotion(null);
    const attrs = useAttrs();
    const layoutGroup = injectLayoutGroup({});
    const config = useMotionConfig();
    function getLayoutId() {
      if (layoutGroup.id && props.layoutId)
        return `${layoutGroup.id}-${props.layoutId}`;
      return props.layoutId || void 0;
    }
    function getProps() {
      return {
        ...props,
        layoutId: getLayoutId(),
        transition: props.transition ?? config.value.transition,
        layoutGroup,
        motionConfig: config.value,
        inViewOptions: props.inViewOptions ?? config.value.inViewOptions,
        animatePresenceContext,
        initial: animatePresenceContext.initial === false ? animatePresenceContext.initial : props.initial === true ? void 0 : props.initial
      };
    }
    function getMotionProps() {
      return {
        ...attrs,
        ...getProps()
      };
    }
    const state = new MotionState(
      getMotionProps(),
      parentState
    );
    provideMotion(state);
    const instance = getCurrentInstance().proxy;
    onBeforeMount(() => {
      state.beforeMount();
    });
    onMounted(() => {
      state.mount(getMotionElement(instance.$el), getMotionProps(), checkMotionIsHidden(instance));
    });
    onBeforeUnmount(() => state.beforeUnmount());
    onUnmounted(() => {
      const el = getMotionElement(instance.$el);
      if (!(el == null ? void 0 : el.isConnected)) {
        state.unmount();
      }
    });
    onBeforeUpdate(() => {
      state.beforeUpdate();
    });
    onUpdated(() => {
      state.update(getMotionProps());
    });
    function getAttrs() {
      const isSVG = state.visualElement.type === "svg";
      const attrsProps = { ...attrs };
      Object.keys(attrs).forEach((key) => {
        if (isMotionValue(attrs[key]))
          attrsProps[key] = attrs[key].get();
      });
      let styleProps = {
        ...props.style,
        ...isSVG ? {} : state.visualElement.latestValues
      };
      if (isSVG) {
        const { attributes, style } = convertSvgStyleToAttributes({
          ...state.isMounted() ? state.target : state.baseTarget,
          ...styleProps
        });
        Object.assign(attrsProps, attributes);
        styleProps = style;
      }
      if (props.drag && props.dragListener !== false) {
        Object.assign(styleProps, {
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          touchAction: props.drag === true ? "none" : `pan-${props.drag === "x" ? "y" : "x"}`
        });
      }
      attrsProps.style = createStyles(styleProps);
      return attrsProps;
    }
    const PrimitiveRef = ref();
    function onMotionComplete(e) {
      var _a, _b;
      if (props.asChild) {
        (_a = PrimitiveRef.value) == null ? void 0 : _a.$forceUpdate();
      }
      (_b = attrs.onMotioncomplete) == null ? void 0 : _b.call(attrs, e);
    }
    return (_ctx, _cache) => {
      var _a;
      return openBlock(), createBlock(unref(Primitive), {
        ref_key: "PrimitiveRef",
        ref: PrimitiveRef,
        "get-props": getProps,
        "get-attrs": getAttrs,
        as: _ctx.as,
        "as-child": _ctx.asChild,
        "data-motion-group": ((_a = unref(layoutGroup).key) == null ? void 0 : _a.value) || void 0,
        onMotioncomplete: onMotionComplete
      }, {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 8, ["as", "as-child", "data-motion-group"]);
    };
  }
});
export {
  _sfc_main as default
};
